import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Button, ErrorNote, Field, Input, PageLoader } from '@/components/ui'

type Mode = 'signin' | 'register'

export default function Login() {
  const { user, loading, setUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()

  const inviteCode = params.get('invite') ?? ''
  const next = params.get('next') ?? (location.state as { from?: string } | null)?.from ?? '/'

  const [mode, setMode] = useState<Mode>(inviteCode ? 'register' : 'signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  const [busy, setBusy] = useState(false)

  if (loading) return <PageLoader />
  if (user) return <Navigate to={next} replace />

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setFieldErrors({})
    try {
      if (mode === 'signin') {
        const res = await api.login(email, password)
        setUser(res.user)
        navigate(next, { replace: true })
      } else {
        const res = await api.register({
          email,
          password,
          displayName,
          ...(inviteCode ? { inviteCode } : {}),
        })
        setUser(res.user)
        navigate(res.joinedCampaignId ? `/campaigns/${res.joinedCampaignId}` : next, { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        setFieldErrors(err.fieldErrors ?? {})
      } else {
        setError('Something went wrong')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span aria-hidden className="mx-auto mb-5 grid size-14 place-items-center rounded-[18px] bg-gold-500 font-display text-base font-bold text-on-accent shadow-[0_8px_32px_-8px] shadow-gold-500/50">
            20
          </span>
          <h1 className="font-display text-[28px] font-semibold">Campaign Hub</h1>
          <p className="mt-1.5 text-sm text-ink-400">
            {inviteCode ? 'Create an account to join the table' : 'Everything around the table, in one place'}
          </p>
        </div>

        <div className="panel panel-lit p-5">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-[14px] bg-ink-900/70 p-1">
            {(['signin', 'register'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => { setMode(value); setError(null); setFieldErrors({}) }}
                className={
                  'rounded-lg py-1.5 text-[13px] font-medium transition-colors ' +
                  (mode === value ? 'bg-ink-750 text-ink-100 shadow-sm' : 'text-ink-400 hover:text-ink-200')
                }
              >
                {value === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <Field label="Display name" error={fieldErrors.displayName?.[0]}>
                {(id) => (
                  <Input id={id} value={displayName} autoComplete="nickname" required maxLength={60}
                    onChange={(e) => setDisplayName(e.target.value)} placeholder="What the table calls you" />
                )}
              </Field>
            )}

            <Field label="Email" error={fieldErrors.email?.[0]}>
              {(id) => (
                <Input id={id} type="email" value={email} autoComplete="email" required
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              )}
            </Field>

            <Field
              label="Password"
              hint={mode === 'register' ? 'At least 10 characters' : undefined}
              error={fieldErrors.password?.[0]}
            >
              {(id) => (
                <Input id={id} type="password" value={password} required
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  onChange={(e) => setPassword(e.target.value)} />
              )}
            </Field>

            {error && <ErrorNote>{error}</ErrorNote>}

            <Button type="submit" variant="primary" busy={busy} className="w-full">
              {mode === 'signin' ? 'Sign in' : 'Create account'}
            </Button>
          </form>
        </div>

        {inviteCode && (
          <p className="mt-4 text-center text-xs text-ink-500">
            Joining with code <span className="font-mono text-gold-400">{inviteCode}</span> ·{' '}
            <Link to={`/join/${inviteCode}`} className="underline hover:text-ink-300">see the campaign</Link>
          </p>
        )}
      </div>
    </div>
  )
}
